import { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, Clock, Instagram, Send, CheckCircle, AlertCircle, MessageSquare } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import SEO from '../components/SEO';
import Breadcrumb from '../components/Breadcrumb';
import { buildBreadcrumbJsonLd } from '../lib/seoSchema';
import { pageSeoMeta } from '@shared/pageSeoMeta';

export default function ContactPage() {
  const { isDark } = useTheme();
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [status, setStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('sending');

    const subject = encodeURIComponent(form.subject || 'Website Inquiry');
    const body = encodeURIComponent(
      `Name: ${form.name}\nEmail: ${form.email}\n\n${form.message}`
    );
    window.location.href = `mailto:zerosmoke2026@gmail.com?subject=${subject}&body=${body}`;

    setTimeout(() => {
      setStatus('sent');
      setForm({ name: '', email: '', subject: '', message: '' });
      setTimeout(() => setStatus('idle'), 4000);
    }, 1000);
  };

  const inp = `w-full px-4 py-3 rounded-xl border text-sm outline-none transition-colors ${
    isDark
      ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-500 focus:border-emerald-500'
      : 'bg-white border-gray-200 text-gray-900 placeholder-gray-400 focus:border-emerald-500'
  }`;

  return (
    <div className="min-h-screen">
      <SEO
        {...pageSeoMeta.contact}
        jsonLd={buildBreadcrumbJsonLd([
          { name: 'Home', url: '/' },
          { name: 'Contact', url: '/contact' },
        ])}
      />

      <Breadcrumb items={[
        { name: 'Home', href: '/' },
        { name: 'Contact' },
      ]} />
      {/* Hero */}
      <section className="hero-gradient py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <span className="text-emerald-400 text-sm font-semibold uppercase tracking-wider">Get in Touch</span>
            <h1 className="text-4xl sm:text-5xl font-extrabold text-white mt-3 mb-6">Contact Us</h1>
            <p className="text-gray-400 text-lg leading-relaxed max-w-2xl mx-auto">
              Have a question, feedback, or need help with an order? We'd love to hear from you.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className={`py-20 transition-colors duration-300 ${isDark ? 'bg-gray-950' : 'bg-white'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-5 gap-12">

            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="lg:col-span-2 space-y-8"
            >
              <div>
                <h2 className={`text-2xl font-bold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  We're Here to Help
                </h2>
                <p className={`text-sm leading-relaxed ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  Whether it's about an order, product question, or just feedback — drop us a message and we'll get back to you as soon as possible.
                </p>
              </div>

              {[
                {
                  icon: <Mail size={20} className="text-emerald-500" />,
                  title: 'Email Us',
                  detail: 'zerosmoke2026@gmail.com',
                  sub: 'Best way to reach us',
                  href: 'mailto:zerosmoke2026@gmail.com',
                },
                {
                  icon: <Clock size={20} className="text-blue-500" />,
                  title: 'Response Time',
                  detail: 'Within 24 hours',
                  sub: 'Mon – Sat, 10 AM – 7 PM IST',
                },
                {
                  icon: <Instagram size={20} className="text-pink-500" />,
                  title: 'Instagram',
                  detail: '@zerosmoke.in_2026',
                  sub: 'DM us anytime',
                  href: 'https://www.instagram.com/zerosmoke.in_2026/',
                },
              ].map((item) => (
                <div key={item.title} className={`flex gap-4 p-4 rounded-xl ${isDark ? 'bg-gray-900' : 'bg-gray-50'}`}>
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${isDark ? 'bg-gray-800' : 'bg-white shadow-sm'}`}>
                    {item.icon}
                  </div>
                  <div>
                    <h3 className={`font-semibold text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>{item.title}</h3>
                    {item.href ? (
                      <a href={item.href} target="_blank" rel="noopener noreferrer" className="text-emerald-500 text-sm hover:underline">
                        {item.detail}
                      </a>
                    ) : (
                      <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{item.detail}</p>
                    )}
                    <p className={`text-xs mt-0.5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{item.sub}</p>
                  </div>
                </div>
              ))}

              {/* FAQ hint */}
              <div className={`p-4 rounded-xl border ${isDark ? 'border-gray-800 bg-gray-900/50' : 'border-gray-200 bg-gray-50'}`}>
                <div className="flex items-center gap-2 mb-2">
                  <MessageSquare size={16} className="text-amber-500" />
                  <h4 className={`font-semibold text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>Common Questions</h4>
                </div>
                <ul className={`text-xs space-y-1.5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                  <li>• Orders can be cancelled within 12 hours of placement</li>
                  <li>• Delivery takes 5–10 business days across Country</li>
                  <li>• Free shipping on orders above ₹5,000</li>
                  <li>• All products are 100% authentic & sealed</li>
                </ul>
              </div>
            </motion.div>

            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="lg:col-span-3"
            >
              <div className={`rounded-2xl p-6 sm:p-8 ${isDark ? 'bg-gray-900 border border-gray-800' : 'bg-white border border-gray-200 shadow-sm'}`}>
                <h3 className={`text-lg font-bold mb-6 ${isDark ? 'text-white' : 'text-gray-900'}`}>Send us a Message</h3>

                {status === 'sent' && (
                  <div className="flex items-center gap-2 bg-emerald-500/10 text-emerald-500 p-3 rounded-lg mb-4 text-sm">
                    <CheckCircle size={16} /> Your email client should have opened. If not, email us directly at zerosmoke2026@gmail.com
                  </div>
                )}
                {status === 'error' && (
                  <div className="flex items-center gap-2 bg-red-500/10 text-red-500 p-3 rounded-lg mb-4 text-sm">
                    <AlertCircle size={16} /> Something went wrong. Please email us directly.
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className={`block text-xs font-medium mb-1.5 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Your Name</label>
                      <input
                        type="text"
                        name="name"
                        value={form.name}
                        onChange={handleChange}
                        placeholder="John Doe"
                        className={inp}
                        required
                      />
                    </div>
                    <div>
                      <label className={`block text-xs font-medium mb-1.5 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Your Email</label>
                      <input
                        type="email"
                        name="email"
                        value={form.email}
                        onChange={handleChange}
                        placeholder="you@example.com"
                        className={inp}
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className={`block text-xs font-medium mb-1.5 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Subject</label>
                    <select
                      name="subject"
                      value={form.subject}
                      onChange={handleChange}
                      className={inp}
                      required
                    >
                      <option value="">Select a topic</option>
                      <option value="Order Issue">Order Issue</option>
                      <option value="Product Question">Product Question</option>
                      <option value="Shipping & Delivery">Shipping & Delivery</option>
                      <option value="Refund / Return">Refund / Return</option>
                      <option value="Feedback">Feedback</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>

                  <div>
                    <label className={`block text-xs font-medium mb-1.5 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Message</label>
                    <textarea
                      name="message"
                      value={form.message}
                      onChange={handleChange}
                      placeholder="Describe your question or concern..."
                      rows={5}
                      className={`${inp} resize-none`}
                      required
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={status === 'sending'}
                    className="w-full flex items-center justify-center gap-2 py-3 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 text-white font-semibold rounded-xl text-sm transition-colors"
                  >
                    {status === 'sending' ? (
                      <><div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" /> Sending...</>
                    ) : (
                      <><Send size={16} /> Send Message</>
                    )}
                  </button>

                  <p className={`text-xs text-center ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                    This will open your email client. You can also email us directly at{' '}
                    <a href="mailto:zerosmoke2026@gmail.com" className="text-emerald-500 hover:underline">zerosmoke2026@gmail.com</a>
                  </p>
                </form>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}
